import { useState, useRef, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { LogOut, ScanLine, CheckCircle2, XCircle, Clock } from "lucide-react";

export default function RfidTerminal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [rfid, setRfid] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);

  // Auto-focus RFID input
  useEffect(() => { inputRef.current?.focus(); }, [result]);

  const handleTap = async (cardId) => {
    const id = (cardId || rfid).trim();
    if (!id) return;
    setRfid("");
    try {
      const { data } = await api.post("/rfid-monitor/tap", { rfid_card_id: id });
      setResult(data);
      setHistory(h => [{ ...data, ts: new Date().toLocaleTimeString() }, ...h].slice(0, 20));
    } catch (e) {
      setResult({ allowed: false, reason: formatErr(e), rfid: id });
    }
    // Auto-clear after 8 seconds
    setTimeout(() => setResult(null), 8000);
  };

  const allowed = result?.allowed;

  return (
    <div className={`w-screen h-screen flex flex-col transition-colors duration-500 ${
      result === null ? "bg-ink" : allowed ? "bg-emerald-600" : "bg-red-600"
    }`}>
      {/* Header */}
      <div className="h-14 flex items-center justify-between px-5 bg-black/20">
        <div className="flex items-center gap-3">
          <BrandLogo size="sm" className="text-white" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 bg-white/10 px-2 py-0.5 rounded-full">RFID Monitor</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-white/80">{user?.name}</span>
          <button data-testid="rfid-logout" onClick={() => { logout(); nav("/"); }} className="p-2 rounded-full hover:bg-white/10 text-white"><LogOut size={16} /></button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {result === null ? (
          /* Waiting state */
          <div className="text-center">
            <ScanLine size={80} className="mx-auto text-white/40 mb-6" />
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white">Tap RFID Card</h1>
            <p className="text-lg text-white/60 mt-3">Waiting for student card…</p>
          </div>
        ) : allowed ? (
          /* Allowed - Green */
          <div className="text-center text-white" data-testid="rfid-result-allowed">
            <CheckCircle2 size={96} className="mx-auto mb-4" />
            <h1 className="font-heading text-5xl font-extrabold">{result.student_name}</h1>
            <div className="text-xl mt-2 opacity-90">{result.grade} · {result.student_id}</div>
            <div className="mt-4 bg-white/20 rounded-2xl px-6 py-4 inline-block">
              <div className="text-sm uppercase tracking-wider opacity-75">Meal approved</div>
              <div className="text-2xl font-extrabold mt-1">{result.plan_name}</div>
              <div className="text-sm mt-1 opacity-80">
                {result.current_slot} · {result.is_preorder ? "Pre-order for today" : "Subscription"}
              </div>
              {result.nutrition && (
                <div className="text-xs mt-2 opacity-70">
                  {result.nutrition.calories} kcal · {result.nutrition.protein}g protein · {result.nutrition.carbs}g carbs
                </div>
              )}
            </div>
            <div className="mt-4 text-sm opacity-70">Used: {result.used_count || 0} meals | Period: {result.start_date} → {result.end_date}</div>
          </div>
        ) : (
          /* Blocked - Red */
          <div className="text-center text-white" data-testid="rfid-result-blocked">
            <XCircle size={96} className="mx-auto mb-4" />
            <h1 className="font-heading text-5xl font-extrabold">{result.student_name || "Access Denied"}</h1>
            {result.grade && <div className="text-xl mt-2 opacity-90">{result.grade} · {result.student_id}</div>}
            <div className="mt-4 bg-white/20 rounded-2xl px-6 py-4 inline-block">
              <div className="text-2xl font-extrabold">{result.reason}</div>
              {result.current_slot && <div className="text-sm mt-2 opacity-80">Current slot: {result.current_slot} ({result.current_time})</div>}
            </div>
          </div>
        )}
      </div>

      {/* RFID Input (hidden but functional - captures scanner input) */}
      <div className="p-4 bg-black/20">
        <form onSubmit={(e) => { e.preventDefault(); handleTap(); }} className="max-w-md mx-auto flex gap-2">
          <input ref={inputRef} data-testid="rfid-input" value={rfid} onChange={e => setRfid(e.target.value)}
            placeholder="Scan or type RFID…" autoFocus
            className="flex-1 rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white placeholder:text-white/40 font-mono focus:outline-none focus:border-white/50" />
          <button type="submit" data-testid="rfid-submit" className="px-5 py-3 rounded-xl bg-white/20 text-white font-bold hover:bg-white/30">
            <ScanLine size={20} />
          </button>
        </form>
        {/* Quick test buttons */}
        <div className="flex justify-center gap-2 mt-2">
          {["RFID-001", "RFID-002"].map(r => (
            <button key={r} data-testid={`rfid-quick-${r}`} onClick={() => handleTap(r)}
              className="text-[11px] px-3 py-1 rounded-full bg-white/10 text-white/70 font-mono hover:bg-white/20">
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* History sidebar */}
      {history.length > 0 && (
        <div className="absolute right-4 top-20 w-72 max-h-[calc(100vh-120px)] overflow-y-auto bg-black/40 backdrop-blur-md rounded-2xl p-3 space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 px-2 py-1">Recent taps</div>
          {history.map((h, i) => (
            <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs ${h.allowed ? "bg-emerald-500/30 text-emerald-100" : "bg-red-500/30 text-red-100"}`}>
              {h.allowed ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
              <span className="font-semibold truncate flex-1">{h.student_name || h.rfid}</span>
              <span className="text-[10px] opacity-70">{h.ts}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
